import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { apiUrl } from '../utils/api';
import { useAuth } from './AuthContext';

export interface Stream {
  id: string;
  name: string;
  status: 'Бурение' | 'Стоп' | 'Не в сети' | 'Ошибка';
  stream_url?: string;
}

export interface StreamInstance extends Stream {
  instanceId: number;
}

interface BroadcastCamera {
  id: string;
  name: string;
  rig_id: string;
  stream_url: string;
  is_online: boolean;
  resolution?: string;
  position_description?: string;
}
interface BroadcastResponse {
  code: number;
  message: string;
  cameras: BroadcastCamera[];
}

interface StreamsState {
  availableStreams: Stream[];
  selectedStreams: StreamInstance[];
  addStream: (streamId: string) => void;
  removeStream: (instanceId: number) => void;
  refreshAvailable: () => Promise<void>;
}

const StreamsContext = createContext<StreamsState | undefined>(undefined);

const SELECTED_KEY = 'streams:selected';

export const StreamsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const token = user?.access_token;
  const [availableStreams, setAvailableStreams] = useState<Stream[]>([]);
  const [selectedStreams, setSelectedStreams] = useState<StreamInstance[]>(() => {
    try {
      const raw = localStorage.getItem(SELECTED_KEY);
      if (!raw) return [];
      const arr: StreamInstance[] = JSON.parse(raw);
      return Array.isArray(arr) ? arr : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(SELECTED_KEY, JSON.stringify(selectedStreams));
    } catch {}
  }, [selectedStreams]);

  const mapCameraToStream = (cam: BroadcastCamera): Stream => ({
    id: cam.id,
    name: cam.name,
    status: cam.is_online ? 'Бурение' : 'Не в сети',
    stream_url: cam.stream_url,
  });

  const refreshAvailable = async () => {
    if (!token) return;
    try {
      const res = await fetch(apiUrl('/broadcast'), {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: token ? `Bearer ${token}` : '',
        },
      });
      if (!res.ok) throw new Error(`Broadcast fetch failed: ${res.status}`);
      const data: BroadcastResponse = await res.json();
      const streams = (data.cameras || []).map(mapCameraToStream);
      setAvailableStreams(streams);
      // If no selection yet, auto-select first up to 4
      setSelectedStreams(prev => {
        if (prev.length > 0) return prev;
        return streams.slice(0, 4).map((s, idx) => ({ ...s, instanceId: Date.now() + idx }));
      });
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    refreshAvailable();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const addStream = (streamId: string) => {
    const stream = availableStreams.find(s => s.id === streamId);
    if (stream && selectedStreams.length < 4 && !selectedStreams.some(s => s.id === stream.id)) {
      setSelectedStreams([...selectedStreams, { ...stream, instanceId: Date.now() }]);
    }
  };

  const removeStream = (instanceId: number) => {
    setSelectedStreams(selectedStreams.filter(s => s.instanceId !== instanceId));
  };

  const value = useMemo(
    () => ({ availableStreams, selectedStreams, addStream, removeStream, refreshAvailable }),
    [availableStreams, selectedStreams]
  );

  return <StreamsContext.Provider value={value}>{children}</StreamsContext.Provider>;
};

export const useStreams = () => {
  const ctx = useContext(StreamsContext);
  if (!ctx) throw new Error('useStreams must be used within StreamsProvider');
  return ctx;
};
